import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputAdornment, MenuItem, TablePagination } from "@mui/material";
import Loader from "../../../components/common/loader/Loader";
import { StyledWelcomeBanner, StyledWrapper } from "../../dashboard/style";
import {
  StyledBoxStyle,
  StyledCustomerSearch,
  StyledOrdersContainer,
  StyledStatusFilter,
  StyledTableLayout,
} from "./style";
import {
  StyledSearchIcon,
  StyledTypographyText,
} from "../../virtual-locker/style";
import { CompactStatistics } from "../../../components/admin/dashboard/StatisticsComponent";
import { lockerStatisticsData, statisticsTitles } from "../../dashboard/constant";
import { VIRTUAL_LOCKER_LIST } from "../../../actions/admin/customer-actions";
import VirtualLockerTable from "../../../components/common/table/VirtualLockerTable";
import VirtualLockerActions from "../../../components/common/table/VirtualLockerActions";
import {
  formatToLongDate,
  VIRTUAL_LOCKER_STATUS_FILTER_OPTIONS,
  virtualLockerStatusColors,
} from "../../../utils/common";
import DotIcon from "../../../assets/svg/dot-icon.svg";
import * as _ from "lodash";

const VirtualLockerManagement = () => {
  const dispatch = useDispatch();
  const [statisticsData, setStatisticsData] = useState(lockerStatisticsData);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const lockerListFunc = useCallback(() => {
    dispatch({
      type: VIRTUAL_LOCKER_LIST,
      payload: {
        search: debouncedSearch,
        page,
        limit: rowsPerPage,
      },
    });
  }, [dispatch, debouncedSearch, page, rowsPerPage]);

  useEffect(() => {
    lockerListFunc();
  }, [lockerListFunc]);

  const { recordList: virtualLockerList, lockerStatistics, total, isLoading } = useSelector(
    (state) => state?.locker
  );

  const columns = [
    "NO",
    "LOCKER ID",
    "CUSTOMER NAME",
    "EXPIRY DATE",
    "NUMBER OF ITEMS",
    "STATUS",
    "ACTION",
  ];
  const columnKeys = [
    "orderId",
    "locker_id",
    "customer_name",
    "expiry_date",
    "items_count",
    "status",
    "action",
  ];

  const resolveCustomerName = (item) =>
    item?.user?.name ||
    [item?.user?.first_name, item?.user?.last_name].filter(Boolean).join(" ").trim() ||
    item?.create_by_user?.name ||
    "—";

  const filteredLockerList = useMemo(() => {
    const list = virtualLockerList || [];
    if (statusFilter === "all") return list;
    return list.filter(
      (item) =>
        String(item.status ?? item.locker_status ?? "inactive").toLowerCase() ===
        statusFilter
    );
  }, [virtualLockerList, statusFilter]);

  const allRecords = filteredLockerList.map((item, index) => {
    const statusKey = String(
      item.status ?? item.locker_status ?? "inactive"
    ).toLowerCase();
    return {
      id: item.id,
      orderId: (page - 1) * rowsPerPage + index + 1,
      locker_id: item.locker_id,
      customer_name: resolveCustomerName(item),
      expiry_date: item.locker_expiry_date
        ? formatToLongDate(item.locker_expiry_date) || "—"
        : "—",
      items_count: item.items_count ?? 0,
      statusValue: statusKey,
      status: (
        <span
          style={{
            background: virtualLockerStatusColors[statusKey] || "#FF8500",
            fontWeight: "bold",
            display: "inline-flex",
            color: "white",
            padding: "2px 8px 2px 6px",
            borderRadius: "20px",
            minWidth: "65px",
            textAlign: "center",
            alignItems: "center",
            gap: 5,
          }}
        >
          <img src={DotIcon} alt="" />
          {_.startCase(statusKey)}
        </span>
      ),
      action: (
        <VirtualLockerActions
          id={item.id}
          status={statusKey}
          onUpdated={lockerListFunc}
        />
      ),
    };
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const nextStatistics = lockerStatisticsData.map((item) => {
      switch (item.title) {
        case statisticsTitles.totalLocker:
          return { ...item, value: lockerStatistics?.total_lockers || 0 };
        default:
          return item;
      }
    });
    setStatisticsData(nextStatistics);
  }, [lockerStatistics]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const paginationCount =
    statusFilter === "all" ? total || 0 : filteredLockerList.length;

  return (
    <StyledWrapper>
      <StyledWelcomeBanner>
        <CompactStatistics idDataList={statisticsData || []} />
      </StyledWelcomeBanner>

      <StyledTableLayout>
        <StyledTypographyText>VIRTUAL LOCKER</StyledTypographyText>

        <StyledBoxStyle>
          <StyledCustomerSearch
            placeholder="Search by locker assignments"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <StyledStatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            displayEmpty
            inputProps={{ "aria-label": "Filter by status" }}
          >
            {VIRTUAL_LOCKER_STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </StyledStatusFilter>
        </StyledBoxStyle>

        <StyledOrdersContainer>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <VirtualLockerTable
                columns={columns}
                columnKeys={columnKeys}
                rows={allRecords}
              />
              <TablePagination
                component="div"
                count={paginationCount}
                page={page - 1}
                onPageChange={(event, newPage) => setPage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </StyledOrdersContainer>
      </StyledTableLayout>
    </StyledWrapper>
  );
};

export default VirtualLockerManagement;
